import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTextMessage, fetchUserSessions, generateSummary, searchSessions } from "../../services/api";
import SpinnerLoader from "../common/spinner/Spinner"
import { NewChat } from "./icons";
import { EventSourceMessage } from "@microsoft/fetch-event-source";
import { useRecoilState, useRecoilValue } from "recoil";
import session from "../../recoil/Session";
import engineState from "../../recoil/SelectedEngine";

interface SearchResult {
    content: string;
    created_at: string;
    rank: number;
    role: string;
    session_id: string;
    token_count: number;
    updated_at: string;
    uuid: string;
}

interface Session {
    created_at: string;
    deleted_at: string | null;
    id: number;
    metadata: {
        summary: string;
    };
    session_id: string;
    updated_at: string;
    user_id: string;
    uuid: string;
}

const ChatPage: FC = () => {
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loader, setLoader] = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [search, setSearch] = useState<string>('');
    const { session_id } = useParams<{ session_id: string }>(); // Extract UUID from the URL
    const navigate = useNavigate();
    const [state, setState] = useRecoilState(session);
    const selectedEngine = useRecoilValue(engineState);
    const controller = useRef<AbortController>(new AbortController());

    const handleSearch = async () => {
        if (search.length == 0) return;
        setLoader(true)
        const response = await searchSessions(search);
        if (!response) {
            setLoader(false);
            return;
        }
        setSearchResults(response.data);
        setLoader(false);
    }

    const handleBackClick = () => {
        setSearch('');
        setSearchResults([]);
    };

    // Function to handle session click and optionally add the message_id as a query parameter
    const handleSessionClick = (session_id: string, message_id?: string) => {
        if (message_id) navigate(`/chat/${session_id}?message_id=${message_id}`); // Navigate to the session detail page with the message_id as a query parameter
        else navigate(`/chat/${session_id}`); // Navigate to the session detail page
    };

    useEffect(() => {
        const fetchData = async () => {
            const result = await fetchUserSessions();
            let data = result?.data ?? [];

            // if a session with the session_id from the url doesnt exist, add it to the front of the array
            if (session_id && !data.find((session: { session_id: string; }) => session.session_id === session_id)) {
                data.unshift({
                    "session_id": session_id
                });
            }

            setSessions(data);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (state.isClosed) {
            const session = sessions.find((session: { session_id: string; }) => session.session_id === state.session);

            // check if session.metadata.summary is a non empty string
            if (session && session.metadata?.summary.length > 0) {
                return;
            } else if (session) {
                generateSummary(selectedEngine.text).then((response) => {

                    let sessionText = response.data.summary;
                    let index = 0;


                    const interval = setInterval(() => {
                        if (index < sessionText.length) {
                            setSessions(sessions.map(s =>
                                s.session_id === state.session ?
                                    {
                                        ...s,
                                        metadata: {
                                            ...s.metadata,
                                            summary: sessionText.substring(0, index)
                                        }
                                    } : s));
                            index++;
                        } else {
                            clearInterval(interval);
                        }
                    }, 50);
                })
            }
        }
    }, [state]);

    return (
        <div className="flex flex-col w-1/5 shadow-md shadow-outline-normal rounded-lg overflow-y-auto">
            <button
                className="flex flex-row items-center py-4 px-2 border-b-2 pl-4 bg-primary text-button-text"
                onClick={() => window.location.assign('/')} // Navigate to the new chat page
            >
                <div className="w-5 h-5 ml-5"><NewChat fillColor={'var(--secondary)'} /></div>
                <p className="text-sm ml-3 font-bold">New Chat</p>
            </button>
            {searchResults.length > 0 && (
                <button
                    onClick={handleBackClick}
                    className="py-2 px-4 bg-gray-200 text-gray-700 font-semibold rounded hover:bg-gray-300 transition duration-300"
                >
                    Back
                </button>
            )}

            <div className="border-b-2 py-4 px-2">
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    type="text"
                    placeholder="search"
                    className="py-2 px-2 pl-4 border-2 border-gray-200 rounded-2xl w-full"
                />
            </div>
            {!loader ? (
                searchResults.length > 0 ? (
                    searchResults.map((result) => (
                        <div
                            className="flex flex-row py-4 px-2 justify-center items-center border-b-2 cursor-pointer"
                            key={result.uuid}
                            onClick={() => handleSessionClick(result.session_id, result.uuid)}
                        >
                            <div className="w-full pl-4">
                                <div className="text-lg font-semibold">
                                    {result.content}
                                </div>
                                <span className="text-gray-500">{new Date(result.updated_at).toLocaleString()}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    sessions.map((session) => (
                        <div
                            className="flex flex-row py-4 px-2 justify-center items-center border-b-2 cursor-pointer"
                            key={session.session_id}
                            onClick={() => handleSessionClick(session.session_id)}
                        >
                            <div className="w-full pl-4">
                                <div className="text-lg font-semibold text-primary">
                                    {typeof session.metadata?.summary === 'string' ? session.metadata.summary : "Chat"}
                                </div>
                                {/* only show if session.updated_at has a value */}
                                {session.updated_at && (
                                    <span className="text-gray-500">{new Date(session.updated_at).toLocaleString()}</span>
                                )}
                            </div>
                        </div>
                    ))
                )
            ) : (
                <SpinnerLoader />
            )}
        </div>
    );
};

export default ChatPage;